import { captureException } from "@sentry/react";

import { describeTransferError, findTransferError, getResponseStatus, isTransferCancelled } from "./sdk-errors";

/**
 * Canonical reporter for fire-and-forget async failures — anything caught from a promise
 * the UI is not awaiting (preload, prefetch, optimistic background sync, dev-mode dynamic
 * imports, etc). Use instead of bare `.catch(() => {})` or `.catch(console.error)`.
 *
 * Routes to Sentry with a stable `fire_and_forget_label` tag so dashboards can group by
 * label. In dev, mirrors to `console.error` so failures surface during local work.
 *
 * Ordinary status-bearing failures are covered by backend monitoring. Transfer failures
 * retain only safe classification because their original objects can carry credentials.
 */
export function reportAsyncError(label: string, error: unknown, context?: Record<string, unknown>): void {
  if (isTransferCancelled(error)) return;
  const transferError = findTransferError(error);
  const safeError = transferError ? new Error(describeTransferError(error)) : error;
  const safeContext = transferError ? transferDiagnostics(transferError, context) : context;
  if (import.meta.env.DEV) {
    console.error(`[fire-and-forget:${label}]`, safeError, safeContext);
  }

  if (!transferError && getResponseStatus(error) !== undefined) return;

  captureException(safeError, {
    tags: { fire_and_forget_label: label },
    extra: safeContext,
  });
}

function transferDiagnostics(
  error: NonNullable<ReturnType<typeof findTransferError>>,
  context?: Record<string, unknown>,
): Record<string, unknown> {
  const kinds = [
    "TransferCancelledError",
    "TransferFailedError",
    "ContentVerificationError",
    "FileChangedDuringUploadError",
    "ContentTooLargeError",
    "PartPlanError",
    "DataPlaneError",
  ];
  const kind = kinds.includes(error.name) ? error.name : "TransferError";
  const diagnostics: Record<string, unknown> = { kind };
  const status = getResponseStatus(error) ?? error.status;
  if (typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599) {
    diagnostics.status = status;
  }
  if (
    "attempts" in error &&
    typeof error.attempts === "number" &&
    Number.isSafeInteger(error.attempts) &&
    error.attempts > 0
  ) {
    diagnostics.attempts = error.attempts;
  }
  const phases = ["queued", "hashing", "uploading", "downloading", "verifying", "committing"];
  if (typeof context?.phase === "string" && phases.includes(context.phase)) diagnostics.phase = context.phase;
  return diagnostics;
}
